import {
    BadgeCheck,
    ChevronDown,
    ChevronRight,
    ChevronUp,
    Edit3,
    Image as ImageIcon,
    Lock,
    Search,
    ShieldCheck,
    Star,
    ThumbsUp,
    X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { RootState } from "../../../../store";
import { Review, RatingPercentage } from "../../../../Redux/products review/types";
import {
    getProductReviewPercentage,
    getProductOverallRating,
    getReviewsByProductId,
} from "../../../../Redux/products review/slice";
import { fetchMyOrders } from "../../../../Redux/custmoer_orders/slice";
import {
    Order,
    CustomerDeliveryStatus,
    PaymentStatus,
} from "../../../../Redux/custmoer_orders/types";
import { getCookie } from "../../../../utility/getCookie";
import { ProductDetailData } from "../ProductDetailTypes";
import "./ProductReviews.css";

export type MappedReview = {
    id: string | number;
    name: string;
    avatar?: string;
    rating: number;
    title: string;
    date: string;
    size?: string;
    color?: string;
    description: string;
    helpful: number;
    verified: boolean;
    images: string[];
};

type ProductReviewsProps = {
    product: ProductDetailData;
};

const getInitials = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return "C";
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return trimmed[0].toUpperCase();
};

const ProductReviews = ({ product }: ProductReviewsProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const {
        productReviews,
        productRating,
        productReviewPercentage,
    } = useAppSelector((state: RootState) => state.productsReview);

    const { user } = useAppSelector((state: RootState) => state.customerAuth);
    const { orders } = useAppSelector((state: RootState) => state.customerOrders);

    const token = getCookie("access_token") || localStorage.getItem("access_token");
    const isLoggedIn = Boolean(
        user ||
        (token &&
            token !== "undefined" &&
            token !== "null" &&
            typeof token === "string" &&
            token.length > 20)
    );

    const INITIAL_VISIBLE_COUNT = 5;
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoadingAll, setIsLoadingAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("recent");
    const [helpfulVotes, setHelpfulVotes] = useState<Record<string, number>>({});
    const [votedReviews, setVotedReviews] = useState<Record<string, boolean>>({});
    const [selectedGalleryImage, setSelectedGalleryImage] = useState<string | null>(null);

    // Fetch review data for this product if needed
    useEffect(() => {
        if (product.id) {
            const strId = String(product.id);
            dispatch(getProductReviewPercentage(strId));
            dispatch(getProductOverallRating(strId));
            dispatch(getReviewsByProductId({ productId: strId, page: 1, limit: 10 }));
        }
    }, [dispatch, product.id]);

    // Fetch customer orders to verify purchase eligibility if logged in
    useEffect(() => {
        if (isLoggedIn) {
            dispatch(fetchMyOrders({ page: 1, limit: 100 }));
        }
    }, [dispatch, isLoggedIn]);

    // Check if the current user purchased this product with completed delivery or payment
    const eligibleOrder = useMemo<Order | null>(() => {
        if (!isLoggedIn || !Array.isArray(orders) || orders.length === 0 || !product?.id) {
            return null;
        }

        const targetIds = [
            product.id,
            (product as any)._id,
            (product as any).ssin,
            (product as any).productId,
        ]
            .filter(Boolean)
            .map((id) => String(id).toLowerCase().trim());

        return (
            orders.find((order) => {
                const delivery = String(order.deliveryStatus || "").trim().toUpperCase();
                const payment = String(order.paymentStatus || "").trim().toUpperCase();

                // Exclude cancelled, refunded, or failed orders
                if (
                    delivery === CustomerDeliveryStatus.CANCELLED ||
                    delivery === "CANCELLED" ||
                    payment === PaymentStatus.CANCELLED ||
                    payment === "CANCELLED" ||
                    payment === PaymentStatus.REFUNDED ||
                    payment === "REFUNDED" ||
                    payment === PaymentStatus.FAILED ||
                    payment === "FAILED"
                ) {
                    return false;
                }

                // Verify the order contains this product
                const hasProduct =
                    Array.isArray(order.productDetails) &&
                    order.productDetails.some((p) => {
                        const pid = String(p.productId || (p as any).id || (p as any)._id || "")
                            .toLowerCase()
                            .trim();
                        return targetIds.includes(pid);
                    });

                if (!hasProduct) return false;

                // Condition: Order delivery must be complete OR payment must be complete
                const isOrderCompleted =
                    delivery === CustomerDeliveryStatus.DELIVERED ||
                    delivery === "DELIVERED" ||
                    delivery === "COMPLETED";

                const isPaymentCompleted =
                    payment === PaymentStatus.COMPLETED ||
                    payment === "COMPLETED" ||
                    payment === "PAID" ||
                    payment === "SUCCESS" ||
                    payment === "CAPTURED";

                return isOrderCompleted || isPaymentCompleted;
            }) || null
        );
    }, [isLoggedIn, orders, product]);

    const myExistingReview = useMemo(() => {
        if (!user || !Array.isArray(productReviews)) return null;
        const currentUserId = String(user._id || (user as any).id || "").trim();
        if (!currentUserId) return null;
        return (
            productReviews.find(
                (r: Review) => String(r.customerId).trim() === currentUserId
            ) || null
        );
    }, [user, productReviews]);

    const reviewDiffInDays = useMemo(() => {
        if (!myExistingReview || !myExistingReview.createdAt) return 999;
        const diffMs = Date.now() - new Date(myExistingReview.createdAt).getTime();
        return diffMs / (1000 * 60 * 60 * 24);
    }, [myExistingReview]);

    const canEditExistingReview = reviewDiffInDays <= 5;
    const remainingEditDays = Math.max(0, Math.ceil(5 - reviewDiffInDays));

    const canWriteReview = Boolean(eligibleOrder) || Boolean(myExistingReview);

    const handleWriteReviewClick = () => {
        if (!isLoggedIn) {
            navigate("/SignIn", {
                state: { returnUrl: `/DetailPage/${product.id}` },
            });
            return;
        }
        if (!canWriteReview) {
            return;
        }
        navigate(`/ProductReviewPage/${product.id}`, {
            state: {
                orderId: eligibleOrder?.orderId || (eligibleOrder as any)?.id,
                orderNo: eligibleOrder?.orderNo,
                matchedOrder: eligibleOrder,
                existingReview: myExistingReview,
            },
        });
    };

    const averageRating = productRating?.averageRating ?? product.rating;
    const totalReviewCount = productRating?.totalReviews ?? (product.reviews ? Number(product.reviews) : 0);
    const hasRatings = Boolean(totalReviewCount && totalReviewCount > 0 && averageRating && averageRating > 0);
    const displayScore = hasRatings && averageRating ? averageRating.toFixed(1) : "-";
    const displayTotalCount = totalReviewCount ? totalReviewCount.toLocaleString() : "0";

    const dynamicBars = useMemo(() => {
        if (productReviewPercentage?.ratingDistribution?.length) {
            return [5, 4, 3, 2, 1].map((stars) => {
                const dist = productReviewPercentage.ratingDistribution.find(
                    (d: RatingPercentage) => d.star === stars
                );
                return {
                    stars,
                    count: dist?.count ?? 0,
                    percentage: dist ? Math.round(dist.percentage) : 0,
                };
            });
        }
        return [5, 4, 3, 2, 1].map((stars) => ({
            stars,
            count: 0,
            percentage: 0,
        }));
    }, [productReviewPercentage]);

    const activeReviews = useMemo<MappedReview[]>(() => {
        if (Array.isArray(productReviews) && productReviews.length > 0) {
            return productReviews.map((r: Review, index: number) => {
                const authorName =
                    (typeof r.customerName === "string" && r.customerName.trim()) ||
                    (typeof (r as any).name === "string" && (r as any).name.trim()) ||
                    (typeof r.productCreatedBy === "string" && r.productCreatedBy.trim()
                        ? r.productCreatedBy.trim()
                        : "Salar Customer");
                const rawDesc = typeof r.description === "string" ? r.description.trim() : "";
                const dateStr = r.createdAt
                    ? new Date(r.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                      })
                    : "-";

                const variationSnapshot = r.purchasedVariationSnapshot || {};
                const size = variationSnapshot.Size || variationSnapshot.size;
                const color = variationSnapshot.Color || variationSnapshot.color || variationSnapshot.Colour;

                let title = `${r.rating || 5} out of 5 stars`;
                if (rawDesc) {
                    const firstPeriod = rawDesc.indexOf(".");
                    if (firstPeriod > 0 && firstPeriod <= 60) {
                        title = rawDesc.slice(0, firstPeriod);
                    } else if (rawDesc.length <= 60) {
                        title = rawDesc;
                    } else {
                        title = `${rawDesc.slice(0, 50)}...`;
                    }
                }

                return {
                    id: r._id || r.revRatingNo || `rev-${index + 1}`,
                    name: authorName,
                    avatar: r.customerAvatar || r.avatar || undefined,
                    rating: Number(r.rating || 5),
                    title,
                    date: dateStr,
                    size: typeof size === "string" && size.trim() ? size.trim() : undefined,
                    color: typeof color === "string" && color.trim() ? color.trim() : undefined,
                    description: rawDesc || "-",
                    helpful: 0,
                    verified: Boolean(r.isVerifiedPurchase),
                    images: Array.isArray(r.images) ? r.images.filter(Boolean) : [],
                };
            });
        }
        return [];
    }, [productReviews]);

    // Gather genuine customer-uploaded images from real reviews only
    const customerImages = useMemo(() => {
        const list: { url: string; author: string; reviewId: string | number }[] = [];
        activeReviews.forEach((rev) => {
            (rev.images || []).forEach((imgUrl) => {
                if (typeof imgUrl === "string" && imgUrl.trim()) {
                    list.push({
                        url: imgUrl.trim(),
                        author: rev.name,
                        reviewId: rev.id,
                    });
                }
            });
        });
        return list;
    }, [activeReviews]);

    const positivePercentage = useMemo(() => {
        if (!hasRatings) return null;
        if (productReviewPercentage?.ratingDistribution?.length) {
            const five = productReviewPercentage.ratingDistribution.find((d) => d.star === 5)?.percentage || 0;
            const four = productReviewPercentage.ratingDistribution.find((d) => d.star === 4)?.percentage || 0;
            return Math.round(five + four);
        }
        if (activeReviews.length > 0) {
            const positiveCount = activeReviews.filter((r) => r.rating >= 4).length;
            return Math.round((positiveCount / activeReviews.length) * 100);
        }
        return null;
    }, [hasRatings, productReviewPercentage, activeReviews]);

    const handleHelpfulClick = (reviewId: string | number) => {
        const key = String(reviewId);
        if (votedReviews[key]) return;
        setVotedReviews((prev) => ({ ...prev, [key]: true }));
        setHelpfulVotes((prev) => ({
            ...prev,
            [key]: (prev[key] || 0) + 1,
        }));
    };

    const filteredReviews = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();

        const filtered = activeReviews.filter(
            (review) =>
                review.name.toLowerCase().includes(query) ||
                review.title.toLowerCase().includes(query) ||
                review.description.toLowerCase().includes(query)
        );

        if (sortBy === "highest") {
            return [...filtered].sort((a, b) => b.rating - a.rating);
        }

        if (sortBy === "lowest") {
            return [...filtered].sort((a, b) => a.rating - b.rating);
        }

        if (sortBy === "helpful") {
            return [...filtered].sort(
                (a, b) =>
                    (b.helpful + (helpfulVotes[String(b.id)] || 0)) -
                    (a.helpful + (helpfulVotes[String(a.id)] || 0))
            );
        }

        return filtered;
    }, [activeReviews, searchTerm, sortBy, helpfulVotes]);

    const visibleReviews = useMemo(() => {
        if (isExpanded) {
            return filteredReviews;
        }
        return filteredReviews.slice(0, INITIAL_VISIBLE_COUNT);
    }, [filteredReviews, isExpanded]);

    const handleToggleExpandReviews = async () => {
        if (!isExpanded) {
            setIsExpanded(true);
            const strId = String(product?.id || "");
            if (strId && productReviews.length < (totalReviewCount || 10)) {
                setIsLoadingAll(true);
                try {
                    await dispatch(
                        getReviewsByProductId({
                            productId: strId,
                            page: 1,
                            limit: 100,
                        })
                    ).unwrap();
                } catch (err) {
                    console.warn("Failed to fetch all reviews:", err);
                } finally {
                    setIsLoadingAll(false);
                }
            }
        } else {
            setIsExpanded(false);
            const feedEl = document.getElementById("customer-reviews-feed");
            if (feedEl) {
                feedEl.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    };

    return (
        <section className="salar-reviews">
            <div className="salar-reviews-inner">
                <div className="salar-reviews-heading">
                    <div>
                        <span>BUYER EXPERIENCE</span>
                        <h2>Ratings & customer stories</h2>
                    </div>

                    <p>
                        Real feedback from customers who purchased and experienced
                        this product.
                    </p>
                </div>

                <div className="reviews-layout">
                    <aside className="reviews-left-column">
                        <div className="review-score-card">
                            <span className="review-score-label">Salar trust score</span>

                            <div className="review-score-value">
                                <strong>{displayScore}</strong>
                                <span>{displayScore !== "-" ? "/ 5" : ""}</span>
                            </div>

                            <div className="review-score-stars">
                                {Array.from({ length: 5 }).map((_, index) => (
                                    <Star
                                        key={index}
                                        size={19}
                                        fill={
                                            hasRatings && averageRating && index < Math.round(averageRating)
                                                ? "currentColor"
                                                : "none"
                                        }
                                    />
                                ))}
                            </div>

                            <p>
                                {hasRatings ? (
                                    <>
                                        Based on <strong>{displayTotalCount}</strong> verified and global customer{" "}
                                        {totalReviewCount === 1 ? "rating" : "ratings"}.
                                    </>
                                ) : (
                                    "No customer ratings submitted yet."
                                )}
                            </p>

                            <div className="score-rank">
                                <BadgeCheck size={16} />
                                <span>
                                    {hasRatings
                                        ? "Customer rated for quality and value"
                                        : "Be the first to rate this product"}
                                </span>
                            </div>

                            <div className="review-bars">
                                {dynamicBars.map((item) => (
                                    <div className="review-bar-row" key={item.stars}>
                                        <span>{item.stars} ★</span>

                                        <div className="review-bar-track">
                                            <div
                                                className="review-bar-fill"
                                                style={{
                                                    width: `${item.percentage}%`,
                                                }}
                                            />
                                        </div>

                                        <strong>{item.percentage}%</strong>
                                    </div>
                                ))}
                            </div>

                            {myExistingReview ? (
                                canEditExistingReview ? (
                                    <div className="review-write-block review-write-block-eligible">
                                        <div className="review-write-eligible-badge">
                                            <BadgeCheck size={14} />
                                            <span>Review Submitted • Editable</span>
                                        </div>
                                        <h3>Your product review</h3>
                                        <p>
                                            You have <strong>{remainingEditDays} day(s) left</strong> to edit your rating or feedback.
                                        </p>

                                        <button
                                            type="button"
                                            onClick={handleWriteReviewClick}
                                        >
                                            Edit your review
                                        </button>
                                    </div>
                                ) : (
                                    <div className="review-write-block review-write-block-locked">
                                        <div className="review-write-locked-badge">
                                            <BadgeCheck size={14} />
                                            <span>Review Submitted</span>
                                        </div>
                                        <h3>Your product review</h3>
                                        <p>
                                            Thank you for your feedback! The 5-day edit window has ended.
                                        </p>

                                        <button
                                            type="button"
                                            onClick={handleWriteReviewClick}
                                            style={{
                                                width: "100%",
                                                padding: "10px 14px",
                                                borderRadius: "6px",
                                                border: "1px solid #d5d9d9",
                                                background: "#f7fafa",
                                                color: "#0f1111",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                cursor: "pointer",
                                            }}
                                        >
                                            View your review
                                        </button>
                                    </div>
                                )
                            ) : canWriteReview ? (
                                <div className="review-write-block review-write-block-eligible">
                                    <div className="review-write-eligible-badge">
                                        <BadgeCheck size={14} />
                                        <span>Verified Buyer</span>
                                    </div>
                                    <h3>Reviewed your purchase?</h3>
                                    <p>Share your experience to help fellow shoppers.</p>

                                    <button
                                        type="button"
                                        onClick={handleWriteReviewClick}
                                    >
                                        Write your review
                                    </button>
                                </div>
                            ) : isLoggedIn ? (
                                <div className="review-write-block review-write-block-locked">
                                    <div className="review-write-locked-badge">
                                        <Lock size={13} />
                                        <span>Verified Purchase Required</span>
                                    </div>
                                    <h3>Have you bought this?</h3>
                                    <p>
                                        Only customers who purchased this item with completed payment or delivery can write a review.
                                    </p>

                                    <button
                                        type="button"
                                        disabled
                                        className="review-write-btn-disabled"
                                        title="Complete your order and payment/delivery to review this product."
                                    >
                                        Verified Buyers Only
                                    </button>
                                </div>
                            ) : (
                                <div className="review-write-block">
                                    <h3>Used this product?</h3>
                                    <p>Sign in to verify your purchase and review.</p>

                                    <button
                                        type="button"
                                        onClick={handleWriteReviewClick}
                                    >
                                        Sign in to write a review
                                    </button>
                                </div>
                            )}
                        </div>
                    </aside>

                    <div className="reviews-right-column">
                        {/* Only render Buyer Insight Card when genuine reviews exist */}
                        {hasRatings && positivePercentage !== null && (
                            <section className="buyer-insight-card">
                                <div className="buyer-insight-top">
                                    <div>
                                        <span>REVIEW INSIGHTS</span>
                                        <h3>What buyers liked most</h3>
                                    </div>

                                    <strong>{positivePercentage}% positive</strong>
                                </div>

                                <p>
                                    Based on verified buyer feedback,{" "}
                                    {positivePercentage}% of customers gave this{" "}
                                    {product.category && product.category !== "-"
                                        ? product.category.toLowerCase()
                                        : "product"}{" "}
                                    a positive rating of 4 stars or higher.
                                </p>

                                <div className="review-insights">
                                    {dynamicBars
                                        .filter((bar) => bar.percentage > 0)
                                        .map((bar) => (
                                            <div
                                                className="review-insight positive"
                                                key={bar.stars}
                                            >
                                                <span>★</span>
                                                <strong>{bar.stars} Star</strong>
                                                <small>{bar.percentage}%</small>
                                            </div>
                                        ))}
                                </div>
                            </section>
                        )}

                        {/* Only render Buyer Gallery when real customer images exist */}
                        {customerImages.length > 0 && (
                            <section className="buyer-gallery">
                                <div className="buyer-gallery-head">
                                    <div>
                                        <span>FROM CUSTOMERS</span>
                                        <h3>Customer photo gallery ({customerImages.length})</h3>
                                    </div>
                                </div>

                                <div className="buyer-gallery-list">
                                    {customerImages.slice(0, 8).map((item, index) => (
                                        <button
                                            type="button"
                                            className="buyer-gallery-item"
                                            key={`${item.url}-${index}`}
                                            onClick={() => setSelectedGalleryImage(item.url)}
                                            title={`Photo by ${item.author}`}
                                        >
                                            <img
                                                src={item.url}
                                                alt={`Customer photo ${index + 1}`}
                                                onError={(e) => {
                                                    (e.currentTarget as HTMLElement).style.display = "none";
                                                }}
                                            />

                                            <span className="gallery-photo-label">
                                                <ImageIcon size={12} />
                                                {item.author}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </section>
                        )}

                        <section className="customer-review-feed">
                            <div className="review-feed-title">
                                <span>CUSTOMER VOICES</span>
                                <h2>Recent product experiences</h2>
                                <p>Detailed feedback shared by Salar shoppers.</p>
                            </div>

                            {activeReviews.length > 0 && (
                                <div className="review-list-toolbar">
                                    <div className="review-search">
                                        <Search size={18} strokeWidth={1.8} />

                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(event) =>
                                                setSearchTerm(event.target.value)
                                            }
                                            placeholder="Search customer reviews"
                                        />
                                    </div>

                                    <select
                                        value={sortBy}
                                        onChange={(event) =>
                                            setSortBy(event.target.value)
                                        }
                                    >
                                        <option value="recent">Most recent</option>
                                        <option value="highest">Highest rated</option>
                                        <option value="lowest">Lowest rated</option>
                                        <option value="helpful">Most helpful</option>
                                    </select>
                                </div>
                            )}

                            <div className="review-feed-list" id="customer-reviews-feed">
                                {visibleReviews.length > 0 ? (
                                    visibleReviews.map((review) => {
                                        const isVoted = Boolean(votedReviews[String(review.id)]);
                                        const totalHelpful =
                                            review.helpful + (helpfulVotes[String(review.id)] || 0);

                                        return (
                                            <article
                                                className="review-feed-card"
                                                key={review.id}
                                            >
                                                <div className="review-author">
                                                    {review.avatar ? (
                                                        <img
                                                            src={review.avatar}
                                                            alt={review.name}
                                                            onError={(e) => {
                                                                (e.currentTarget as HTMLElement).style.display = "none";
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="review-author-avatar-initials">
                                                            {getInitials(review.name)}
                                                        </div>
                                                    )}

                                                    <div>
                                                        <strong>{review.name}</strong>
                                                        <span>Salar shopper</span>
                                                    </div>

                                                    {review.verified && (
                                                        <span className="verified-badge">
                                                            <BadgeCheck size={14} />
                                                            Verified Purchase
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="review-heading-line">
                                                    <div className="review-stars">
                                                        {Array.from({ length: 5 }).map((_, index) => (
                                                            <Star
                                                                key={index}
                                                                size={15}
                                                                fill={
                                                                    index < review.rating
                                                                        ? "currentColor"
                                                                        : "none"
                                                                }
                                                            />
                                                        ))}
                                                    </div>

                                                    <h3>{review.title}</h3>
                                                </div>

                                                <div className="review-submeta">
                                                    <span>{review.date}</span>

                                                    {review.size && (
                                                        <span>
                                                            Size <strong>{review.size}</strong>
                                                        </span>
                                                    )}

                                                    {review.color && (
                                                        <span>
                                                            Colour <strong>{review.color}</strong>
                                                        </span>
                                                    )}
                                                </div>

                                                <p className="review-body">
                                                    {review.description}
                                                </p>

                                                {review.images.length > 0 && (
                                                    <div className="review-upload-list">
                                                        {review.images.map((image, index) => (
                                                            <button
                                                                type="button"
                                                                key={`${review.id}-${index}`}
                                                                onClick={() => setSelectedGalleryImage(image)}
                                                            >
                                                                <img
                                                                    src={image}
                                                                    alt={`${product.title} review photo ${index + 1}`}
                                                                    onError={(e) => {
                                                                        (e.currentTarget as HTMLElement).style.display = "none";
                                                                    }}
                                                                />
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="review-card-footer">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleHelpfulClick(review.id)}
                                                        disabled={isVoted}
                                                        className={isVoted ? "voted" : ""}
                                                    >
                                                        <ThumbsUp size={15} />
                                                        {isVoted ? "Helpful" : "Helpful"}
                                                        <span>{totalHelpful}</span>
                                                    </button>
                                                </div>
                                            </article>
                                        );
                                    })
                                ) : (
                                    <div className="no-reviews-found">
                                        <Search size={24} />
                                        <strong>
                                            {activeReviews.length === 0
                                                ? "No customer reviews yet"
                                                : "No matching reviews"}
                                        </strong>
                                        <span>
                                            {activeReviews.length === 0
                                                ? "Be the first to share your experience with this product."
                                                : "Try another search term."}
                                        </span>
                                        {activeReviews.length === 0 && (
                                            canWriteReview ? (
                                                <button
                                                    type="button"
                                                    className="write-first-review-btn"
                                                    onClick={handleWriteReviewClick}
                                                >
                                                    Write a Review
                                                </button>
                                            ) : isLoggedIn ? (
                                                <div className="review-verified-only-note">
                                                    <ShieldCheck size={14} />
                                                    <span>Only verified buyers with completed orders or payments can submit a review.</span>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="write-first-review-btn"
                                                    onClick={handleWriteReviewClick}
                                                >
                                                    Sign in to write a review
                                                </button>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="reviews-footer-actions">
                                {(filteredReviews.length > INITIAL_VISIBLE_COUNT ||
                                    totalReviewCount > INITIAL_VISIBLE_COUNT ||
                                    productReviews.length < totalReviewCount) && (
                                    <button
                                        type="button"
                                        className={`load-more-reviews ${isExpanded ? "expanded" : ""}`}
                                        onClick={handleToggleExpandReviews}
                                        disabled={isLoadingAll}
                                    >
                                        {isLoadingAll ? (
                                            <span>Loading all customer reviews...</span>
                                        ) : isExpanded ? (
                                            <>
                                                <span>Show fewer reviews</span>
                                                <ChevronUp size={15} />
                                            </>
                                        ) : (
                                            <>
                                                <span>
                                                    {`View all ${
                                                        totalReviewCount > filteredReviews.length
                                                            ? totalReviewCount
                                                            : filteredReviews.length
                                                    } customer reviews`}
                                                </span>
                                                <ChevronDown size={15} />
                                            </>
                                        )}
                                    </button>
                                )}

                                {/* {canWriteReview && (
                                    <button
                                        type="button"
                                        className="write-review-feed-btn"
                                        onClick={handleWriteReviewClick}
                                    >
                                        <Edit3 size={15} />
                                        <span>
                                            {myExistingReview && canEditExistingReview
                                                ? "Edit your review"
                                                : "Write a review"}
                                        </span>
                                    </button>
                                )} */}
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Lightbox modal for customer review photos */}
            {selectedGalleryImage && (
                <div
                    className="review-image-modal-backdrop"
                    onClick={() => setSelectedGalleryImage(null)}
                >
                    <div
                        className="review-image-modal-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="review-image-modal-close"
                            onClick={() => setSelectedGalleryImage(null)}
                        >
                            <X size={20} />
                        </button>
                        <img
                            src={selectedGalleryImage}
                            alt="Customer review photo full view"
                        />
                    </div>
                </div>
            )}
        </section>
    );
};

export default ProductReviews;